/**
 * Connector Engine Bridge
 *
 * THE missing piece: wires connector-core + connector-adapters into the UI.
 * This is the client-side pipeline that runs inside the ImportWizard.
 *
 * Pipeline: File → Adapter (parse) → Mapper (transform) → Validator → Conflict Resolver → Audit
 */
import {
  RawDataRecord,
  FieldMapping,
  DataEntity,
  TenantContext,
  ConflictStrategy,
  POSProvider,
  ImportSession,
} from '@retailsync/shared-types'
import { parseDataFile } from './file-parser'

// These are dynamically imported to avoid bundling if not needed.
// In dev / mock mode they are loaded on demand.

export interface ParsedFile {
  headers: string[]
  rows: RawDataRecord[]
  totalRows: number
  fileName: string
  fileSize: number
  fileType: 'csv' | 'excel' | 'json'
}

export interface MappedData {
  entity: DataEntity
  records: any[]
  mappings: FieldMapping[]
  headers: string[]
}

export interface ValidationReport {
  valid: boolean
  errors: { row: number; field?: string; message: string; severity: string }[]
  validRecords: any[]
  invalidRecords: any[]
  summary: string
}

export interface PipelineResult {
  parsedFile: ParsedFile
  mappedData: MappedData
  validation: ValidationReport
  syncResult: {
    totalRecords: number
    processedRecords: number
    failedRecords: number
    duration: number
  }
  auditLog: string[]
}

export interface EngineProgress {
  phase: 'idle' | 'parsing' | 'mapping' | 'validating' | 'syncing' | 'complete' | 'error'
  progress: number // 0–100
  message: string
}

export interface ImportHistoryEntry {
  id: string
  entity: DataEntity
  provider: POSProvider
  totalRecords: number
  processedRecords: number
  failedRecords: number
  duration: number
  timestamp: string
  tenantId: string
}

/**
 * The connector engine — client-side pipeline that runs in the browser.
 *
 * Uses the connector-core packages (mapper, validator, conflict-resolver)
 * and connector-adapters (file parsers) directly in the UI.
 *
 * In production, prefer the backend worker path (connectorApi.processUpload)
 * for credential safety and audit compliance. Set VITE_USE_LOCAL_ENGINE=true
 * to force client-side mode.
 */
export class ConnectorEngine {
  private tenant: TenantContext
  private onProgress?: (progress: EngineProgress) => void
  private auditLog: string[] = []
  private session: ImportSession | null = null

  constructor(
    tenant: TenantContext,
    onProgress?: (progress: EngineProgress) => void,
  ) {
    this.tenant = tenant
    this.onProgress = onProgress
  }

  // ─── Pipeline Steps (called sequentially by ImportWizard) ───

  /**
   * Step 1: Parse the uploaded file to detect headers and preview rows.
   */
  async parseFile(file: File): Promise<ParsedFile> {
    this.reportProgress('parsing', 10, 'Reading file…')
    this.audit('PARSE_START', { fileName: file.name, fileSize: file.size })

    // Shared parser: CSV / XLSX / XLS / JSON all normalise to the same shape.
    const parsedFile = await parseDataFile(file)

    const parsed: ParsedFile = {
      headers: parsedFile.headers,
      rows: parsedFile.rows,
      totalRows: parsedFile.totalRows,
      fileName: parsedFile.fileName,
      fileSize: parsedFile.fileSize,
      fileType: parsedFile.fileType,
    }

    this.reportProgress('parsing', 30, `Parsed ${parsed.totalRows} rows, ${parsed.headers.length} columns`)
    this.audit('PARSE_COMPLETE', { fileType: parsed.fileType, headers: parsed.headers, totalRows: parsed.totalRows })

    return parsed
  }

  /**
   * Step 2: Map raw data to RetailsyncGo entities using field mappings.
   */
  async mapData(
    parsedFile: ParsedFile,
    entity: DataEntity,
    mappings: FieldMapping[],
  ): Promise<MappedData> {
    this.reportProgress('mapping', 40, 'Mapping fields…')
    this.audit('MAP_START', { entity, mappingCount: mappings.length })

    // Use connector-core FieldMapper (dynamic import to keep bundle lean)
    const { FieldMapper } = await import('@retailsync/connector-core')
    const mapper = new FieldMapper()

    const records = await mapper.map(this.tenant, entity, parsedFile.rows, mappings)

    const mapped: MappedData = {
      entity,
      records,
      mappings,
      headers: parsedFile.headers,
    }

    this.reportProgress('mapping', 55, `Mapped ${records.length} records to ${entity}`)
    this.audit('MAP_COMPLETE', { recordCount: records.length })

    return mapped
  }

  /**
   * Step 3: Validate mapped data against schemas.
   */
  async validateData(entity: DataEntity, records: any[]): Promise<ValidationReport> {
    this.reportProgress('validating', 60, 'Validating data…')
    this.audit('VALIDATE_START', { entity, recordCount: records.length })

    // Use connector-core SchemaValidator
    const { SchemaValidator } = await import('@retailsync/connector-core')
    const validator = new SchemaValidator()
    const result = await validator.validate(entity, records)

    const report: ValidationReport = {
      valid: result.valid,
      errors: result.errors.map((e: any) => ({
        row: e.row ?? 0,
        field: e.field,
        message: e.message,
        severity: e.severity,
      })),
      validRecords: result.validRecords,
      invalidRecords: result.invalidRecords,
      summary: result.valid
        ? `All ${result.validRecords.length} records passed validation`
        : `${result.validRecords.length} valid, ${result.invalidRecords.length} invalid — ${result.errors.length} error(s)`,
    }

    this.reportProgress('validating', 75, report.summary)
    this.audit('VALIDATE_COMPLETE', {
      valid: result.valid,
      validCount: result.validRecords.length,
      invalidCount: result.invalidRecords.length,
    })

    return report
  }

  /**
   * Step 4: Execute the full sync (with conflict resolution).
   * In client-side mode this is a simulation; real sync happens on the server.
   */
  async executeSync(
    entity: DataEntity,
    provider: POSProvider,
    records: any[],
    strategy: ConflictStrategy = 'external_wins',
  ): Promise<{
    totalRecords: number
    processedRecords: number
    failedRecords: number
    duration: number
  }> {
    this.reportProgress('syncing', 80, 'Starting import…')
    this.audit('SYNC_START', { entity, provider, recordCount: records.length, strategy })

    const startTime = performance.now()

    // Use connector-core DefaultConflictResolver for conflict resolution
    const { DefaultConflictResolver } = await import('@retailsync/connector-core')
    const resolver = new DefaultConflictResolver()

    let processedRecords = 0
    let failedRecords = 0

    for (const record of records) {
      try {
        // Resolve conflicts by comparing with empty internal record
        // (simulating: external data always wins in client-side mode)
        await resolver.resolve(this.tenant, record, {} as any, strategy)
        processedRecords++
      } catch {
        failedRecords++
      }
    }

    const duration = Math.round(performance.now() - startTime)

    this.reportProgress('syncing', 95, `Imported ${processedRecords} records`)
    this.audit('SYNC_COMPLETE', {
      totalRecords: records.length,
      processedRecords,
      failedRecords,
      duration,
    })

    // Persist to import history (sessionStorage)
    this.saveToHistory({
      entity,
      provider,
      totalRecords: records.length,
      processedRecords,
      failedRecords,
      duration,
    })

    return {
      totalRecords: records.length,
      processedRecords,
      failedRecords,
      duration,
    }
  }

  /**
   * Save import result to sessionStorage for the ImportHistory page.
   */
  private saveToHistory(result: {
    entity: DataEntity
    provider: POSProvider
    totalRecords: number
    processedRecords: number
    failedRecords: number
    duration: number
  }) {
    try {
      const key = `connector-history-${this.tenant.tenantId}`
      const stored = sessionStorage.getItem(key)
      const history: ImportHistoryEntry[] = stored ? JSON.parse(stored) : []

      history.unshift({
        id: `import-${Date.now()}`,
        entity: result.entity,
        provider: result.provider,
        totalRecords: result.totalRecords,
        processedRecords: result.processedRecords,
        failedRecords: result.failedRecords,
        duration: result.duration,
        timestamp: new Date().toISOString(),
        tenantId: this.tenant.tenantId,
      })

      // Keep last 50 entries
      sessionStorage.setItem(key, JSON.stringify(history.slice(0, 50)))
    } catch (e) {
      console.warn('[ConnectorEngine] Failed to save history:', e)
    }
  }

  /**
   * Run the full pipeline end-to-end.
   */
  async runPipeline(
    file: File,
    entity: DataEntity,
    mappings: FieldMapping[],
    provider: POSProvider = 'custom',
    strategy: ConflictStrategy = 'external_wins',
  ): Promise<PipelineResult> {
    this.auditLog = []

    // Step 1: Parse
    const parsedFile = await this.parseFile(file)

    // Step 2: Map
    const mappedData = await this.mapData(parsedFile, entity, mappings)

    // Step 3: Validate
    const validation = await this.validateData(entity, mappedData.records)

    // Step 4: Sync
    const syncResult = await this.executeSync(
      entity,
      provider,
      validation.validRecords,
      strategy,
    )

    this.reportProgress('complete', 100, 'Import complete')
    this.audit('PIPELINE_COMPLETE', syncResult)

    return {
      parsedFile,
      mappedData,
      validation,
      syncResult,
      auditLog: this.auditLog,
    }
  }

  // ─── Session Management ───

  setSession(session: ImportSession) {
    this.session = session
  }

  getSession(): ImportSession | null {
    return this.session
  }

  // ─── Private Helpers ───

  private reportProgress(
    phase: EngineProgress['phase'],
    progress: number,
    message: string,
  ) {
    if (this.onProgress) {
      this.onProgress({ phase, progress, message })
    }
  }

  private audit(event: string, data: Record<string, any>) {
    const entry = `[${new Date().toISOString()}] ${event}: ${JSON.stringify(data)}`
    this.auditLog.push(entry)
    console.log(`[ConnectorEngine] ${entry}`)
  }
}

// ─── Factory ───

let engineInstance: ConnectorEngine | null = null

export function getConnectorEngine(
  tenant: TenantContext,
  onProgress?: (progress: EngineProgress) => void,
): ConnectorEngine {
  if (!engineInstance || tenant.tenantId !== engineInstance['tenant'].tenantId) {
    engineInstance = new ConnectorEngine(tenant, onProgress)
  }
  return engineInstance
}

export function createConnectorEngine(
  tenant: TenantContext,
  onProgress?: (progress: EngineProgress) => void,
): ConnectorEngine {
  return new ConnectorEngine(tenant, onProgress)
}

/** Read import history from sessionStorage for a tenant. */
export function getImportHistory(tenantId: string): ImportHistoryEntry[] {
  try {
    const key = `connector-history-${tenantId}`
    const stored = sessionStorage.getItem(key)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

/** Clear import history for a tenant. */
export function clearImportHistory(tenantId: string): void {
  sessionStorage.removeItem(`connector-history-${tenantId}`)
}
